// drift-test:no-test-needed: this commit adds schema_template?: string to
// AttemptTrace — pure type-alias addition mirroring the new Go field
// (orchestrator/internal/models/run_trace.go) and the regenerated openapi
// types. T15 (trace_invariants) asserts the json tag is referenced in
// the admin TSX → covers UI consumption via SchemaTemplateSection.
//
// V2 admin trace API client + typed RunTrace shapes per plan §7.2 + §15 OpenAPI.
//
// These types mirror orchestrator/internal/models/run_trace.go. They are
// hand-authored rather than openapi-fetch-derived because the RunTrace shape
// is described in openapi.yaml as `trace: nullable: true (description-only)`
// at Phase 1.7 scaffold time; the formal schema lands in Phase 1.11 OpenAPI
// deltas. Once openapi has the schema, regenerate types and replace these
// hand-authored ones with the generated counterparts.

import { apiClient } from "../api/client";

export interface RunTrace {
  run_id: string;
  started_at: string;
  completed_at: string | null;
  stages: StageTrace[];
  errors: TraceError[];
}

export interface StageTrace {
  stage: string; // "analyze" | "decide" | "create"
  started_at: string;
  completed_at: string | null;
  // Outcome enumerates how the stage ended. The admin StageRow badge uses
  // this to render OK / FAIL / PANIC / WAIT.
  //   success           — happy path
  //   no_op             — stage ran but produced no work
  //   aborted           — sub-call AbortStage / DAG transport error
  //   aborted_panic     — goroutine panic recovered + re-thrown
  //   aborted_validator — post-DAG validator/stewardship rejected the attempt
  //   aborted_apply     — ApplyCreateDelta / ApplySupersession /
  //                       ApplyCoverageClaim / ApplyTopicRelabelRouting failed
  outcome: string;
  tx_ops: number;
  // Attempt is 0 for non-retried stages (analyze/decide/synthetic) and
  // 1..N for retried stages (CREATE attempt loop). When non-zero the
  // UI surfaces "attempt N" alongside the stage name so multiple
  // same-stage rows are visually distinguishable.
  attempt?: number;
  layers: LayerTrace[];
  total_cost_usd: number;
  total_input_tokens: number;
  total_output_tokens: number;
}

export interface LayerTrace {
  name: string;
  layer_index: number;
  started_at: string;
  completed_at: string | null;
  sub_calls: SubCallTrace[];
  bridge: BridgeTrace | null;
  total_cost_usd: number;
  total_input_tokens: number;
  total_output_tokens: number;
}

export interface SubCallTrace {
  sub_prompt_id: string;
  item_key: string;
  item_index: number;
  prompt_file: string;
  schema_file: string;
  attempts: AttemptTrace[];
  outcome: string; // "success" | "fallback" | "abort" | "canceled"
  fell_back: boolean;
  duration_ms: number;
  total_cost_usd: number;
  total_input_tokens: number;
  total_output_tokens: number;
  // Cascade-origin link: when outcome=="canceled", points at the
  // earliest abort sibling in the same layer (the genuine failure that
  // tore the errgroup down). ExpandPanel's ValidationTab renders an
  // inline "cancelled by → <link>" affordance when both are non-empty.
  originator_sub_prompt_id?: string;
  originator_item_key?: string;
  // 0076 batched sub-call: when is_batched, this ONE row served N items via a
  // single LLM call. The shared prompt/response/cost stay on `attempts`
  // (counted once); per-item outcomes live in item_records. Absent/false =>
  // a classic one-item-one-call row (the entire pre-0076 corpus), so the
  // BatchSubCallRow render gates on is_batched and legacy rows fall through
  // to the existing per-row rendering unchanged.
  is_batched?: boolean;
  item_records?: ItemSubRecord[];
}

// ItemSubRecord is one item's slice of a batched (0076) sub-call. Operator
// surfaces expand these for per-item outcome + parsed_output; cost/tokens are
// NOT on the item (they are batch-shared on SubCallTrace.attempts).
export interface ItemSubRecord {
  item_key: string;
  item_index: number;
  outcome: string;
  fell_back: boolean;
  parsed_output: unknown;
  attempts: AttemptTrace[];
}

// PromptTemplate field marker — the AttemptTrace interface below adds
// `prompt_template?: string` carrying the contents of the prompt file
// at execution time. Empty when the file couldn't be read on the
// orchestrator side; PromptSHA still pins the intended version.

export interface AttemptTrace {
  attempt: number;
  started_at: string;
  duration_ms: number;
  prompt_sent: string;
  prompt_sent_ref: string;
  prompt_sent_sha256: string;
  prompt_sent_write_outcome: string;
  prompt_template?: string;
  // SchemaTemplate carries the contents of the output schema file at
  // execution time. Empty/absent when the orchestrator couldn't read
  // it; schema_sha still pins the intended version for diffing.
  schema_template?: string;
  raw_response: string;
  raw_response_ref: string;
  raw_response_sha256: string;
  parsed_output: unknown;
  parsed_output_ref: string;
  input_tokens: number | null;
  output_tokens: number | null;
  cost_usd: number | null;
  // Prompt-cache hit count: the part of input_tokens served from the provider's prompt cache, priced at the model's cached-input rate (a fraction of the input rate).
  // null = field absent from CLI event stream or no hit reported.
  // 0 = no cache hit. N>0 = N input tokens served from cache.
  cached_input_tokens: number | null;
  // Internal CLI API turns for this attempt (0091; claude's num_turns).
  // Optional (omitempty in the Go model) — absent/null when not reported.
  num_turns?: number | null;
  // Answered from the pass's stored answers, no new tokens (0064); absent when false.
  reused?: boolean;
  adapter_name: string;
  model_name: string;
  effort_level: string;
  prompt_sha: string;
  schema_sha: string;
  feedback: string;
  feedback_kind: string; // "" | "schema" | "semantic"
  error: string;
}

export interface BridgeTrace {
  name: string;
  started_at: string;
  duration_ms: number;
  steps: BridgeStepTrace[];
  outcome: string; // "success" | "abort"
  error: string;
}

export interface BridgeStepTrace {
  step_id: string;
  description: string;
  input_summary: Record<string, unknown>;
  output_summary: Record<string, unknown>;
  mutations: string[];
  before_after_subtree_ref: string;
  duration_ms: number;
  error: string;
}

export interface TraceError {
  stage: string;
  source: string; // "sub_call" | "bridge" | "validator" | "supervisor"
  node_id: string;
  code: string;
  detail: string;
  // 0062: severity classification. "fatal" = run-terminating; "warning"
  // = expected/handled (e.g. node_count_shortfall from a BestOfN retry).
  // Server-side classifier is TraceErrorSeverityFor in run_trace.go.
  severity?: "fatal" | "warning";
}

export async function fetchRunTrace(
  uid: string,
  pid: string,
  rid: string,
): Promise<{ trace: RunTrace | null; run: unknown }> {
  const { data, error } = await apiClient.GET(
    "/api/v1/admin/users/{uid}/products/{pid}/runs/{rid}",
    {
      params: {
        path: { uid, pid, rid },
        // openapi-typescript types may not yet include "trace" in the enum
        // (it was added in Phase 1.7 openapi.yaml deltas); cast to bypass.
        query: { include: "trace" as never },
      },
    },
  );
  if (error) throw error;
  return data as { trace: RunTrace | null; run: unknown };
}

export async function fetchTraceBlob(
  uid: string,
  pid: string,
  rid: string,
  ref: string,
): Promise<string> {
  const url = `/api/v1/admin/users/${uid}/products/${pid}/runs/${rid}/trace/blob?ref=${encodeURIComponent(ref)}`;
  const resp = await fetch(url, { credentials: "include", headers: { "X-Requested-With": "xhr" } });
  if (!resp.ok) throw new Error(`trace blob fetch: HTTP ${resp.status}`);
  return resp.text();
}
